import { useEffect, useState, useRef } from 'react'
import { Text, useText } from '@visx/text'
import { FormState } from '../slices/formSlice'
import { useAppSelector } from '../hooks'
import { selectInitialized, selectMediaItem, selectFonts, toggleBackgroundImage } from '../slices/mediaSlice'

interface RenderImageElementProps {
    width?: number
    height?: number
    svgRef?: React.Ref<SVGSVGElement>
}

export type RenderImageProps = RenderImageElementProps & Omit<FormState, 'quote' | 'email'>

function RenderImage({
    width,
    height = width,
    svgRef,
    image,
    statement: quoteOriginal,
    name,
    age,
    origin,
}: RenderImageProps) {
    const viewboxWidth = 1080
    const viewboxHeight = viewboxWidth
    const [imageHeight, setImageHeight] = useState<number>(0)
    const [imageWidth, setImageWidth] = useState<number>(0)
    const margin = {
        top: 0.03 * viewboxHeight,
        imageTop: 0.222 * viewboxHeight,
        left: 0.25 * viewboxWidth,
        right: 0.25 * viewboxWidth,
        between_x: 0.034 * viewboxWidth,
        between_y: 0.13 * viewboxHeight,
        bottom: 100,
    }

    const initialized = useAppSelector(selectInitialized)
    const backgroundImage = useAppSelector(selectMediaItem('layout_top'))
    const fonts = useAppSelector(selectFonts)
    

    const callForAction = {
        width: 400,
        height: 210,
    }
    const hashtagDimensions = {
        width: 1200 / 6,
        height: 199 / 6,
    }
    const quoteTextBox = useRef<SVGTextElement>(null)
    const personTextBox = useRef<SVGTextElement>(null)
    const [personYPosition, setPersonYPosition] = useState(viewboxHeight - margin.bottom)
    const maxImageWidth = 950
    const maxImageHeight = 600

    const subline = [name, age ? `${age} Jahre` : false, origin].filter((e) => e).join(', ')
    const quoteWithoutDoubleWhitespace = quoteOriginal?.replace(/\s+/g, ' ') ?? ''
    const { wordsByLines } = useText({
        fontSize: `${0.05 * viewboxWidth}pt`,
        className: 'quote',
        y: 0.125 * viewboxHeight,
        x: 0.966 * viewboxWidth,
        textAnchor: 'start',
        verticalAnchor: 'start',
        children: quoteWithoutDoubleWhitespace,
    })
    const quoteTruncated =
        wordsByLines.length <= 9
            ? quoteWithoutDoubleWhitespace
            : quoteWithoutDoubleWhitespace.substring(
                  0,
                  quoteWithoutDoubleWhitespace.indexOf(wordsByLines[9].words.join(' ')),
              ) + '...'
    useEffect(() => {
        if (image) {
            const img = new Image()
            img.src = image
            img.onload = () => {
                const scaledWidth = Math.min(img.width, maxImageWidth)
                const scaledHeight = Math.min(img.height, maxImageHeight)
                setImageWidth(scaledWidth)
                setImageHeight(scaledHeight)
            }
        }
    }, [image])

    const imageX = 20
    const imageY = 350

    useEffect(() => {
        setPersonYPosition(
            Math.min(
                quoteTextBox.current
                    ? margin.top + quoteTextBox.current.getBBox().height + margin.between_y
                    : viewboxHeight - margin.bottom,
                viewboxHeight - callForAction.height - hashtagDimensions.height - 35,
            ),
        )
    }, [
        quoteTextBox,
        viewboxHeight,
        quoteTruncated,
        name,
        age,
        origin,
        margin.between_x,
        margin.between_y,
        margin.bottom,
        margin.imageTop,
        margin.top,
        callForAction.width,
        callForAction.height,
        hashtagDimensions.height,
    ])

    if (!initialized) {
        return <></>
    }

    return (
        <svg viewBox={`0 0 ${viewboxWidth} ${viewboxHeight}`} width={width} height={height} ref={svgRef}>
            <style
                dangerouslySetInnerHTML={{
                    __html: `
@font-face {
    font-family: 'Mont Book';
    src: url('${fonts.montbook.woff2}') format('woff2'), url('${fonts.montbook.woff}') format('woff');
}
@font-face {
    font-family: 'Mont Heavy';
    src: url('${fonts.montheavy.woff2}') format('woff2'), url('${fonts.montheavy.woff}') format('woff');
}
@font-face {
    font-family: 'Waiting for the Sunrise';
    src: url('${fonts.waitingforthesunrise.woff2}') format('woff2'), url('${fonts.waitingforthesunrise.woff}') format('woff');
}
.quote {
    fill: #ffee00;
    font-family: 'Waiting for the Sunrise';
    text-align: right;
    text-transform: uppercase;
}
.person {
    fill: #ffee00;
    font-family: 'Waiting for the Sunrise';
    text-transform: uppercase;
}
`,
                }}
            />
            {image && (
                <image
                    href={image}
                    height={570}
                    width={1000}
                    x={imageX}
                    y={imageY}
                    preserveAspectRatio="xMidYMid slice"
                    clipPath="url(#clip-circle)"
                />
            )}
            {backgroundImage && <image href={backgroundImage} height={viewboxHeight} width={viewboxWidth} />}
        </svg>
    )
}

export default RenderImage
